// Which trigger feeds each posting rule of 07 §10.2 (P01–P31). Accounting consumes operational
// events only (02 BR-ARC-006); `internal` rules post from accounting's own documents, optionally
// created from an upstream event. Each event-driven rule has exactly one source so nothing posts twice.
import type { EventType } from './catalogue.ts';

export type PostingTrigger =
  | { readonly kind: 'event'; readonly events: readonly EventType[] }
  | {
      readonly kind: 'internal';
      readonly document: string;
      readonly upstream: readonly EventType[];
    };

export interface PostingRuleSource {
  readonly rule: string;
  /** Event code of the 07 §10.2 table, null where the table names none. */
  readonly code: string | null;
  readonly trigger: PostingTrigger;
}

const event = (...events: EventType[]): PostingTrigger =>
  Object.freeze({ kind: 'event', events: Object.freeze(events) });
const internal = (document: string, ...upstream: EventType[]): PostingTrigger =>
  Object.freeze({ kind: 'internal', document, upstream: Object.freeze(upstream) });

/**
 * Movement-valued rules (P08–P11, P14, P17–P20, P28) read `inventory.movement.posted` (05 §17
 * "valuation → GL via account mapping") and select by movement type; receipt movements are left to
 * P01/P02 so a receipt never posts twice [assumption; Q for W2d.posting].
 */
export const POSTING_RULE_SOURCES: readonly PostingRuleSource[] = Object.freeze([
  Object.freeze({
    rule: 'P01',
    code: 'inv.receipt',
    trigger: event('purchasing.goods_receipt.posted'),
  }),
  Object.freeze({
    rule: 'P02',
    code: 'inv.receipt',
    trigger: event('purchasing.goods_receipt.reversed'),
  }),
  Object.freeze({
    rule: 'P03',
    code: 'ap.invoice',
    trigger: internal('ap_document', 'purchasing.goods_receipt.posted'),
  }),
  Object.freeze({ rule: 'P04', code: null, trigger: internal('ap_document') }),
  Object.freeze({ rule: 'P05', code: null, trigger: internal('ap_document') }),
  Object.freeze({ rule: 'P06', code: null, trigger: internal('ap_document') }),
  Object.freeze({
    rule: 'P07',
    code: null,
    trigger: internal('ap_document', 'shipping.note.posted'),
  }),
  Object.freeze({
    rule: 'P08',
    code: 'inv.supplier_return',
    trigger: event('inventory.movement.posted'),
  }),
  Object.freeze({
    rule: 'P09',
    code: 'inv.issue_to_wip',
    trigger: event('inventory.movement.posted'),
  }),
  Object.freeze({
    rule: 'P10',
    code: 'wip.return_to_inv',
    trigger: event('inventory.movement.posted'),
  }),
  Object.freeze({ rule: 'P11', code: 'wip.to_fg', trigger: event('inventory.movement.posted') }),
  Object.freeze({ rule: 'P12', code: null, trigger: event('production.cut.completed') }),
  Object.freeze({ rule: 'P13', code: null, trigger: event('costing.actual.closed') }),
  Object.freeze({
    rule: 'P14',
    code: 'cogs.wholesale',
    trigger: event('inventory.movement.posted'),
  }),
  Object.freeze({
    rule: 'P15',
    code: 'ar.invoice',
    trigger: internal('ar_document', 'shipping.shipment.ready'),
  }),
  Object.freeze({
    rule: 'P16',
    code: 'ar.credit',
    trigger: internal('ar_document', 'shipping.note.posted'),
  }),
  Object.freeze({ rule: 'P17', code: 'cogs.return', trigger: event('inventory.movement.posted') }),
  Object.freeze({ rule: 'P18', code: 'inv.adjust', trigger: event('inventory.movement.posted') }),
  Object.freeze({ rule: 'P19', code: 'inv.reclass', trigger: event('inventory.movement.posted') }),
  Object.freeze({ rule: 'P20', code: null, trigger: event('inventory.movement.posted') }),
  Object.freeze({
    rule: 'P21',
    code: 'inv.revaluation',
    trigger: internal('landed_cost_allocation'),
  }),
  Object.freeze({ rule: 'P22', code: null, trigger: internal('payment') }),
  Object.freeze({ rule: 'P23', code: null, trigger: internal('payment') }),
  Object.freeze({ rule: 'P24', code: null, trigger: internal('bank_statement') }),
  Object.freeze({ rule: 'P25', code: null, trigger: internal('fx_revaluation') }),
  Object.freeze({ rule: 'P26', code: null, trigger: internal('allowance_accrual') }),
  Object.freeze({ rule: 'P27', code: null, trigger: event('d2c.daily_summary.ready') }),
  Object.freeze({ rule: 'P28', code: 'cogs.d2c', trigger: event('inventory.movement.posted') }),
  Object.freeze({ rule: 'P29', code: null, trigger: event('d2c.payout.received') }),
  Object.freeze({ rule: 'P30', code: null, trigger: internal('opening_balance') }),
  Object.freeze({
    rule: 'P31',
    code: null,
    trigger: internal('year_end_close', 'accounting.period.closed'),
  }),
]);
